import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function BiField({ label, value = {}, onChange, area }) {
  const Comp = area ? Textarea : Input;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <div>
        <Label className="text-xs font-semibold text-stone-500">{label} 🇫🇷</Label>
        <Comp value={value.fr || ""} onChange={(e) => onChange({ ...value, fr: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[44px]" />
      </div>
      <div>
        <Label className="text-xs font-semibold text-stone-500">{label} 🇬🇧</Label>
        <Comp value={value.en || ""} onChange={(e) => onChange({ ...value, en: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[44px]" />
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-3xl border-2 border-stone-200 p-5 space-y-3">
      <h3 className="font-head font-bold text-lg">{title}</h3>
      {children}
    </div>
  );
}

export default function ContentManager() {
  const [c, setC] = useState(null);

  useEffect(() => { api.get("/admin/content").then((r) => setC(r.data.content)); }, []);

  const set = (key, patch) => setC((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));
  const setList = (key, idx, patch) => setC((p) => ({ ...p, [key]: p[key].map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = (key, item) => setC((p) => ({ ...p, [key]: [...(p[key] || []), item] }));
  const removeItem = (key, idx) => setC((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    try { await api.put("/admin/content", c); toast.success("Content saved"); }
    catch (e) { toast.error("Save failed"); }
  };

  if (!c) return <div className="text-stone-400 py-10">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex items-center justify-between sticky top-0 bg-cream/80 backdrop-blur z-10 py-2">
        <p className="text-stone-500 text-sm">Edit the public website content. Changes go live immediately.</p>
        <Button onClick={save} data-testid="content-save" className="rounded-full bt-gradient text-white font-bold"><Save size={16} className="mr-2" />Save</Button>
      </div>

      <Card title="Hero">
        <BiField label="Title" value={c.hero?.title} onChange={(v) => set("hero", { title: v })} area />
        <BiField label="Subtitle" value={c.hero?.sub} onChange={(v) => set("hero", { sub: v })} area />
        <BiField label="Primary CTA" value={c.hero?.cta} onChange={(v) => set("hero", { cta: v })} />
        <BiField label="Secondary CTA" value={c.hero?.cta2} onChange={(v) => set("hero", { cta2: v })} />
      </Card>

      <Card title="Explainer video">
        <div><Label className="text-xs font-semibold text-stone-500">Video URL (leave empty for DEMO placeholder)</Label>
          <Input value={c.explainer?.video_url || ""} onChange={(e) => set("explainer", { video_url: e.target.value })} data-testid="content-explainer-url" className="rounded-xl border-2 border-stone-300 mt-1 min-h-[44px]" /></div>
        <div><Label className="text-xs font-semibold text-stone-500">Poster image URL</Label>
          <Input value={c.explainer?.poster || ""} onChange={(e) => set("explainer", { poster: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[44px]" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!c.explainer?.is_demo} onChange={(e) => set("explainer", { is_demo: e.target.checked })} /> Show DEMO badge</label>
      </Card>

      <Card title="Final call to action">
        <BiField label="Title" value={c.final_cta?.title} onChange={(v) => set("final_cta", { title: v })} area />
        <BiField label="Subtitle" value={c.final_cta?.sub} onChange={(v) => set("final_cta", { sub: v })} area />
        <BiField label="Button" value={c.final_cta?.cta} onChange={(v) => set("final_cta", { cta: v })} />
      </Card>

      <Card title={`FAQ (${(c.faq || []).length})`}>
        {(c.faq || []).map((f, i) => (
          <div key={i} className="rounded-2xl bg-stone-50 p-3 space-y-2" data-testid={`content-faq-${i}`}>
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => removeItem("faq", i)} className="rounded-lg text-red-500"><Trash2 size={14} /></Button></div>
            <BiField label="Question" value={f.q} onChange={(v) => setList("faq", i, { q: v })} />
            <BiField label="Answer" value={f.a} onChange={(v) => setList("faq", i, { a: v })} area />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => addItem("faq", { q: { fr: "", en: "" }, a: { fr: "", en: "" } })} data-testid="content-add-faq" className="rounded-full font-semibold"><Plus size={14} className="mr-1" />Add question</Button>
      </Card>

      <Card title={`Tutorials — BETOOL Academy (${(c.tutorials || []).length})`}>
        {(c.tutorials || []).map((tut, i) => (
          <div key={i} className="rounded-2xl bg-stone-50 p-3 space-y-2" data-testid={`content-tutorial-${i}`}>
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => removeItem("tutorials", i)} className="rounded-lg text-red-500"><Trash2 size={14} /></Button></div>
            <BiField label="Title" value={tut.title} onChange={(v) => setList("tutorials", i, { title: v })} />
            <BiField label="Description" value={tut.desc} onChange={(v) => setList("tutorials", i, { desc: v })} />
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Duration (2:00)" value={tut.duration || ""} onChange={(e) => setList("tutorials", i, { duration: e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
              <Input placeholder="Thumbnail URL" value={tut.thumb || ""} onChange={(e) => setList("tutorials", i, { thumb: e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
              <Input placeholder="Video URL" value={tut.video_url || ""} onChange={(e) => setList("tutorials", i, { video_url: e.target.value, is_demo: !e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => addItem("tutorials", { title: { fr: "", en: "" }, desc: { fr: "", en: "" }, duration: "", thumb: "/mk-explainer-poster.jpg", video_url: "", is_demo: true })} data-testid="content-add-tutorial" className="rounded-full font-semibold"><Plus size={14} className="mr-1" />Add tutorial</Button>
      </Card>

      <Card title={`Community feedback (${(c.testimonials || []).length})`}>
        {(c.testimonials || []).map((tm, i) => (
          <div key={i} className="rounded-2xl bg-stone-50 p-3 space-y-2" data-testid={`content-testimonial-${i}`}>
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => removeItem("testimonials", i)} className="rounded-lg text-red-500"><Trash2 size={14} /></Button></div>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Name" value={tm.name || ""} onChange={(e) => setList("testimonials", i, { name: e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
              <Input placeholder="Country" value={tm.country || ""} onChange={(e) => setList("testimonials", i, { country: e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
              <Input placeholder="Avatar URL" value={tm.avatar || ""} onChange={(e) => setList("testimonials", i, { avatar: e.target.value })} className="rounded-xl border-2 border-stone-300 min-h-[44px]" />
            </div>
            <BiField label="Comment" value={tm.comment} onChange={(v) => setList("testimonials", i, { comment: v })} area />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!tm.verified} onChange={(e) => setList("testimonials", i, { verified: e.target.checked })} /> Verified badge</label>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => addItem("testimonials", { name: "", country: "", avatar: "", verified: false, comment: { fr: "", en: "" } })} data-testid="content-add-testimonial" className="rounded-full font-semibold"><Plus size={14} className="mr-1" />Add feedback</Button>
      </Card>
    </div>
  );
}
